import React, { useState, useEffect } from 'react';
import { 
  Users, Star, ChevronsUp, DollarSign, Smile, Briefcase, AlertTriangle, Award, 
  ChefHat, Clipboard, UserPlus, UserMinus, BookOpen, ThumbsUp, PenTool, Shield,
  Calendar, Wine, Car, Monitor, TrendingUp, Activity, Flower, Sunrise, Coffee, Feather,
  ChevronDown, Search, Filter, X, Check, AlertCircle, Home, Building,
  Mic
} from 'lucide-react';

const StaffManagementTab = ({ 
  selectedTab, 
  staffRoles, 
  hireStaff, 
  fireStaff, 
  staff, 
  setSelectedTab,
  rooms = [],
  guests = [],
  facilities = [],
  formatMoney = (amount) => `${amount.toFixed(2)}`
}) => {
  // Add animation styles for toast notification
  // Note: Normally this would be in a global CSS file, but for this example we'll use inline styles
  const animationStyles = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .animate-fadeIn {
      animation: fadeIn 0.3s ease-out forwards;
    }
  `;

  const [expandedCategory, setExpandedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [showTips, setShowTips] = useState(false);
  const [lastClickedButton, setLastClickedButton] = useState(null);
  const [actionFeedback, setActionFeedback] = useState({ show: false, message: '', type: 'success' });
  
  // Clear feedback message after timeout
  useEffect(() => {
    if (actionFeedback.show) {
      const timer = setTimeout(() => {
        setActionFeedback({ show: false, message: '', type: 'success' });
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [actionFeedback]);
  
  // Visual feedback for button clicks
  const handleButtonClick = (action, id) => {
    setLastClickedButton(id);
    
    // Call the appropriate function and set feedback
    if (action === 'hire') {
      hireStaff(id);
      setActionFeedback({ 
        show: true, 
        message: `${id} was hired successfully`, 
        type: 'success' 
      });
    } else if (action === 'fire') {
      fireStaff(id);
      setActionFeedback({ 
        show: true, 
        message: `Staff member #${id} was removed`, 
        type: 'warning' 
      });
    }
    
    // Clear the button highlight after a short delay
    setTimeout(() => {
      setLastClickedButton(null);
    }, 300);
  };
  
  // Staff role icon mapping
  const getRoleIcon = (role) => {
    switch(role.toLowerCase()) {
      case 'housekeeper': return <Clipboard className="w-4 h-4" />;
      case 'receptionist': return <Smile className="w-4 h-4" />;
      case 'chef': return <ChefHat className="w-4 h-4" />;
      case 'manager': return <Briefcase className="w-4 h-4" />;
      case 'concierge': return <ThumbsUp className="w-4 h-4" />;
      case 'maintenance': return <PenTool className="w-4 h-4" />;
      case 'security': return <Shield className="w-4 h-4" />;
      case 'event coordinator': return <Calendar className="w-4 h-4" />;
      case 'sommelier': return <Wine className="w-4 h-4" />;
      case 'valet': return <Car className="w-4 h-4" />;
      case 'it specialist': return <Monitor className="w-4 h-4" />;
      case 'marketing director': return <TrendingUp className="w-4 h-4" />;
      case 'fitness instructor': return <Activity className="w-4 h-4" />;
      case 'gardener': return <Flower className="w-4 h-4" />;
      case 'guest relations manager': return <Star className="w-4 h-4" />;
      case 'spa therapist': return <Sunrise className="w-4 h-4" />;
      case 'bartender': return <Coffee className="w-4 h-4" />;
      case 'sustainability officer': return <Feather className="w-4 h-4" />;
      case 'singer': return <Mic className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };
  
  // Improved star rendering with proper aria labels for accessibility
  const renderStars = (level) => {
    const starColors = {
      1: 'text-red-400',
      2: 'text-orange-400',
      3: 'text-yellow-400',
      4: 'text-lime-500',
      5: 'text-green-500'
    };
    
    return (
      <div className="flex items-center" aria-label={`Rating ${level.toFixed(1)} out of 5`}>
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-3 h-3 sm:w-4 sm:h-4 ${i < level ? starColors[Math.min(level, 5)] : 'text-gray-200'}`}
            fill={i < level ? 'currentColor' : 'none'}
            aria-hidden="true"
          />
        ))}
        <span className="text-xs ml-1 text-gray-500">{level.toFixed(1)}/5</span>
      </div>
    );
  };
  
  // Improved grouped staff roles with collapsible sections
  const staffCategories = [
    {
      id: 'guest-services',
      title: "Guest Services",
      icon: <Smile className="w-4 h-4" />,
      roles: staffRoles.filter(r => ['receptionist', 'concierge', 'valet', 'guest relations manager'].includes(r.role.toLowerCase()))
    },
    {
      id: 'food-beverage',
      title: "Food & Beverage",
      icon: <ChefHat className="w-4 h-4" />,
      roles: staffRoles.filter(r => ['chef', 'bartender', 'sommelier'].includes(r.role.toLowerCase()))
    },
    {
      id: 'management',
      title: "Management",
      icon: <Briefcase className="w-4 h-4" />,
      roles: staffRoles.filter(r => ['manager', 'marketing director'].includes(r.role.toLowerCase()))
    },
    {
      id: 'facilities',
      title: "Facilities & Maintenance",
      icon: <PenTool className="w-4 h-4" />,
      roles: staffRoles.filter(r => ['housekeeper', 'maintenance', 'gardener', 'sustainability officer', 'it specialist'].includes(r.role.toLowerCase()))
    },
    {
      id: 'wellness',
      title: "Wellness & Activities",
      icon: <Activity className="w-4 h-4" />,
      roles: staffRoles.filter(r => ['singer','spa therapist', 'fitness instructor', 'event coordinator', 'security'].includes(r.role.toLowerCase()))
    }
  ];
  
  // Calculate staffing status based on rooms, guests and facilities
  const getStaffingStatus = () => {
    // Get metrics from props
    const occupiedRooms = rooms.filter(room => room.occupied).length;
    const totalRooms = rooms.length;
    const totalGuests = guests.length;
    const guestToRoomRatio = totalRooms > 0 ? totalGuests / occupiedRooms : 0;
    
    // Calculate recommended staff based on actual hotel metrics
    const recommendedStaff = Math.ceil(
      // Base staff for occupied rooms (higher ratio for luxury hotels with more guests per room)
      (occupiedRooms * (guestToRoomRatio > 1.5 ? 0.3 : 0.2)) + 
      // Staff for facilities
      (facilities.length * 2) + 
      // Additional staff for higher guest volume
      (totalGuests > 50 ? Math.floor(totalGuests / 25) : 0) +
      // Minimum management (3 for small hotels, more for larger ones)
      (totalRooms < 50 ? 2 : totalRooms < 100 ? 3 : 5)
    );
    
    const totalStaff = staff.length;
    const staffRatio = recommendedStaff > 0 ? totalStaff / recommendedStaff : 0;
    
    if (totalStaff === 0) return { 
      status: 'critical', 
      message: 'No staff! Hire immediately', 
      icon: <AlertTriangle className="w-4 h-4" />,
      count: totalStaff,
      recommended: recommendedStaff
    };
    if (staffRatio < 0.5) return { 
      status: 'critical', 
      message: 'Severely understaffed', 
      icon: <AlertTriangle className="w-4 h-4" />,
      count: totalStaff,
      recommended: recommendedStaff
    };
    if (staffRatio < 0.8) return { 
      status: 'warning', 
      message: 'Understaffed', 
      icon: <AlertTriangle className="w-4 h-4" />,
      count: totalStaff,
      recommended: recommendedStaff
    };
    if (staffRatio < 1.1) return { 
      status: 'good', 
      message: 'Adequately staffed', 
      icon: <Award className="w-4 h-4" />,
      count: totalStaff,
      recommended: recommendedStaff
    };
    return { 
      status: 'optimal', 
      message: 'Fully staffed', 
      icon: <Award className="w-4 h-4" />,
      count: totalStaff,
      recommended: recommendedStaff
    };
  };
  
  const staffStatus = getStaffingStatus();
  
  // Handle category toggle
  const toggleCategory = (categoryId) => {
    if (expandedCategory === categoryId) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(categoryId);
    }
  };
  
  // Filter staff based on search term and role filter
  const filteredStaff = staff.filter(employee => {
    const matchesSearch = employee.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         employee.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterRole === '' || employee.role.toLowerCase() === filterRole.toLowerCase();
    return matchesSearch && matchesFilter;
  });
  
  // Get unique roles from current staff for filter dropdown
  const uniqueRoles = [...new Set(staff.map(employee => employee.role))];
  
  return (
    selectedTab === 'staff' && (
      <>
        <style>{animationStyles}</style>
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 md:p-6 transition-all relative">
        {/* Visual feedback toast notification */}
        {actionFeedback.show && (
          <div className={`absolute top-3 right-3 z-50 flex items-center rounded-lg px-4 py-2 shadow-md text-sm transition-all animate-fadeIn
            ${actionFeedback.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 
              actionFeedback.type === 'warning' ? 'bg-amber-50 text-amber-700 border border-amber-200' : 
              'bg-blue-50 text-blue-700 border border-blue-200'}`}>
            {actionFeedback.type === 'success' ? 
              <Check className="w-4 h-4 mr-2" /> : 
              actionFeedback.type === 'warning' ? 
              <AlertCircle className="w-4 h-4 mr-2" /> : 
              <AlertTriangle className="w-4 h-4 mr-2" />}
            {actionFeedback.message}
          </div>
        )}
        
        {/* Header with status overview - Improved for better mobile layout */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
          <div>
            <h2 className="text-lg sm:text-xl font-bold flex items-center">
              <Users className="mr-2 text-blue-600" />
              Staff Management
            </h2>
            <div className={`mt-1 text-xs sm:text-sm flex flex-wrap items-center gap-1
              ${staffStatus.status === 'critical' ? 'text-red-600' : 
                staffStatus.status === 'warning' ? 'text-amber-600' : 'text-green-600'}`}>
              {staffStatus.icon}
              <span className="ml-1">{staffStatus.message}</span>
              <span className="ml-0 sm:ml-2 bg-gray-100 px-2 py-0.5 rounded-full text-gray-800 text-xs">
                {staffStatus.count}/{staffStatus.recommended} recommended staff
              </span>
            </div>
          </div>
        </div>
        
        {/* Hiring section - Redesigned with collapsible categories */}
        <div className="mb-4 sm:mb-6 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-3 sm:p-4 transition-all">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
            <h3 className="font-semibold flex items-center text-sm sm:text-base">
              <UserPlus className="w-4 h-4 mr-2 text-green-600" />
              Hire New Staff
            </h3>
            
          </div>
          
          <div className="space-y-2">
            {staffCategories.map((category) => (
              <div key={category.id} className="bg-white rounded-lg shadow-sm overflow-hidden transition-all">
                <button 
                  onClick={() => toggleCategory(category.id)}
                  className="w-full p-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200"
                  aria-expanded={expandedCategory === category.id}
                  aria-controls={`category-content-${category.id}`}
                >
                  <div className="flex items-center">
                    {category.icon}
                    <h4 className="text-sm font-medium text-gray-700 ml-2">{category.title}</h4>
                    <span className="ml-2 text-xs text-gray-500">({category.roles.length} roles)</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedCategory === category.id ? 'transform rotate-180' : ''}`} />
                </button>
                
                {expandedCategory === category.id && (
                  <div 
                    id={`category-content-${category.id}`}
                    className="p-3 border-t border-gray-100 flex flex-wrap gap-2 transition-all"
                  >
                    {category.roles.map((staffRole) => (
                      <button
                        key={staffRole.role}
                        onClick={() => hireStaff(staffRole.role)}
                        className="flex items-center px-2 py-1.5 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 active:scale-95 rounded-lg transition-all shadow-sm hover:shadow text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1"
                        aria-label={`Hire ${staffRole.role} for ${staffRole.baseSalary} per day`}
                      >
                        {getRoleIcon(staffRole.role)}
                        <span className="ml-1 whitespace-nowrap">Hire {staffRole.role}</span>
                        <span className="ml-1 sm:ml-2 bg-white bg-opacity-20 px-1.5 py-0.5 rounded-full text-xs text-black">
                          ${staffRole.baseSalary}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Staff table - Enhanced with search and filtering */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-2">
            <h3 className="font-semibold flex items-center text-sm sm:text-base">
              <Briefcase className="w-4 h-4 mr-2 text-blue-600" />
              Current Staff
            </h3>
            
            <div className="flex flex-col sm:flex-row gap-2">
              {/* Search box */}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search staff..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 pr-2 py-1 border border-gray-200 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
              
              {/* Role filter */}
              <div className="relative">
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="pl-8 pr-6 py-1 border border-gray-200 rounded-lg text-sm appearance-none w-full focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="">All roles</option>
                  {uniqueRoles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
                <Filter className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
          </div>
          
          <div className="overflow-auto rounded-lg border border-gray-200 bg-white shadow-sm max-h-[calc(100vh-450px)] sm:max-h-[calc(100vh-400px)]">
            {filteredStaff.length > 0 ? (
              <div className="min-w-full divide-y divide-gray-200">
                {/* Desktop view - table format */}
                <table className="min-w-full divide-y divide-gray-200 hidden sm:table">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th scope="col" className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Staff Member</th>
                      <th scope="col" className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance</th>
                      <th scope="col" className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Compensation</th>
                      <th scope="col" className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {filteredStaff.map((employee) => (
                      <tr key={employee.id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                              {getRoleIcon(employee.role)}
                            </div>
                            <div className="ml-3">
                              <div className="font-medium text-gray-900 text-sm">{employee.name}</div>
                              <div className="text-xs text-gray-500 flex flex-wrap items-center gap-1">
                                {employee.role}
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
                                  ID: {employee.id}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="space-y-1">
                            <div className="flex items-center">
                              <span className="text-xs font-medium text-gray-500 w-20">Skill:</span>
                              {renderStars(employee.skill)}
                            </div>
                            <div className="flex items-center">
                              <span className="text-xs font-medium text-gray-500 w-20">Happiness:</span>
                              {renderStars(employee.happiness)}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center font-medium text-gray-900 text-sm">
                            <DollarSign className="w-4 h-4 text-green-500 mr-1" />
                            {employee.salary}/day
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleButtonClick('fire', employee.id)}
                              className={`px-3 py-1.5 bg-red-500 hover:bg-red-400 active:bg-red-600 active:scale-95 text-white rounded-lg text-xs flex items-center shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-1 ${lastClickedButton === employee.id ? 'animate-pulse' : ''}`}
                              aria-label={`Fire ${employee.name}`}
                            >
                              <UserMinus className="w-3 h-3 mr-1" />
                              Fire
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Mobile view - card format */}
                <div className="sm:hidden divide-y divide-gray-200">
                  {filteredStaff.map((employee) => (
                    <div key={employee.id} className="p-3 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                            {getRoleIcon(employee.role)}
                          </div>
                          <div className="ml-2">
                            <div className="font-medium text-gray-900 text-sm">{employee.name}</div>
                            <div className="text-xs text-gray-500">{employee.role}</div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleButtonClick('fire', employee.id)}
                          className={`px-2 py-1 bg-red-500 hover:bg-red-400 active:bg-red-600 active:scale-95 text-white rounded-lg text-xs flex items-center shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-red-400 ${lastClickedButton === employee.id ? 'animate-pulse' : ''}`}
                          aria-label={`Fire ${employee.name}`}
                        >
                          <UserMinus className="w-3 h-3 mr-1" />
                          Fire
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <div className="text-gray-500 mb-1">Performance</div>
                          <div className="flex items-center mb-1">
                            <span className="font-medium text-gray-500 w-16">Skill:</span>
                            {renderStars(employee.skill)}
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium text-gray-500 w-16">Happiness:</span>
                            {renderStars(employee.happiness)}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 mb-1">Details</div>
                          <div className="flex items-center mb-1">
                            <DollarSign className="w-3 h-3 text-green-500 mr-1" />
                            <span className="font-medium">{employee.salary}/day</span>
                          </div>
                          <div className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs inline-block">
                            ID: {employee.id}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-4 sm:p-6 text-center text-gray-500">
                {staff.length === 0 ? (
                  <>
                    <Users className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm">No staff members yet. Hire some staff to improve your hotel's operations!</p>
                  </>
                ) : (
                  <>
                    <Search className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm">No staff matches your search criteria.</p>
                    <button 
                      onClick={() => {setSearchTerm(''); setFilterRole('');}}
                      className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Clear filters
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Tips section - Collapsible for better space management */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 overflow-hidden transition-all">
          <button 
            onClick={() => setShowTips(!showTips)}
            className="w-full p-3 sm:p-4 flex justify-between items-center focus:outline-none focus:bg-blue-100 hover:bg-blue-100 transition-colors"
            aria-expanded={showTips}
            aria-controls="tips-content"
          >
            <h3 className="font-bold flex items-center text-blue-800 text-sm sm:text-base">
              <BookOpen className="w-4 h-4 mr-2" />
              Staff Management Tips
            </h3>
            <ChevronDown className={`w-4 h-4 text-blue-600 transition-transform ${showTips ? 'transform rotate-180' : ''}`} />
          </button>
          
          {showTips && (
            <div id="tips-content" className="p-3 sm:p-4 border-t border-blue-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <h4 className="font-medium mb-1 text-blue-700">Role Benefits</h4>
                  <ul className="space-y-1">
                    <li className="flex items-start">
                      <Clipboard className="w-4 h-4 mr-2 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Housekeepers improve room quality and cleanliness ratings</span>
                    </li>
                    <li className="flex items-start">
                      <Smile className="w-4 h-4 mr-2 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Receptionists boost guest satisfaction and check-in efficiency</span>
                    </li>
                    <li className="flex items-start">
                      <ThumbsUp className="w-4 h-4 mr-2 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Concierges provide premium service for luxury guests</span>
                    </li>
                    <li className="flex items-start">
                      <Wine className="w-4 h-4 mr-2 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Sommeliers enhance bar experience and increase beverage sales</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-1 text-blue-700">Management Strategy</h4>
                  <ul className="space-y-1">
                    <li className="flex items-start">
                      <ChefHat className="w-4 h-4 mr-2 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Chefs are essential for running a profitable restaurant</span>
                    </li>
                    <li className="flex items-start">
                      <Briefcase className="w-4 h-4 mr-2 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Managers improve overall efficiency and staff productivity</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="w-4 h-4 mr-2 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Staff skills improve over time - keeping them happy increases retention</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      </>
    )
  );
};

export default StaffManagementTab;